import { Button } from '@tunasong/ui-lib'
import type { CSSProperties, FC } from 'react'
import { useEffect, useId, useRef } from 'react'
import { MelodyMiniRoll } from '../melody-roll/melody-mini-roll.js'
import type { CapturePhase } from '../pitch-training/capture-session.js'
import { PitchInputMeter, type PitchMeterState } from '../pitch-training/pitch-input-meter.js'
import type { PracticeTarget } from './practice-target.js'
import styles from './phrase-question.module.css'

/**
 * The question screen for a phrase or a part (Pitch Trainer Phase 2, plan M6a step 6.1; Joni §3, §5). The
 * same layout as Phase 1, with up to two status lines under the heading, which takes focus and is
 * described by them, so a key change is announced before Start.
 */

export interface PhraseQuestionProps {
  target: PracticeTarget
  /** Null between takes, when nothing is running */
  phase: CapturePhase | null
  meter: PitchMeterState
  /** The take's cap, for the progress bar */
  captureMs: number
  /** "Sing this section": "phrase 2 of 4", or "part 2 of 4" for chord-only material (M6b) */
  position?: string
  onStart: () => void
  /** Joni §5: the singer can always end a take */
  onDone: () => void
  onHearIt: () => void
}

/** UI status strings, as in Phase 1: what the machine is doing right now */
const phaseText: Record<CapturePhase, string> = {
  'count-in': 'Listen…',
  reference: 'Listen…',
  'lead-in': 'Breathe',
  listening: 'Sing',
  'got-it': 'Got it',
  scoring: 'Scoring…',
}

export const PhraseQuestion: FC<PhraseQuestionProps> = props => {
  const { target, phase, meter, captureMs, position, onStart, onDone, onHearIt } = props
  const headingRef = useRef<HTMLHeadingElement>(null)
  const statusId = useId()
  const busy = phase !== null
  const isListening = phase === 'lead-in' || phase === 'listening'
  const lines = [target.keyLine, target.slowedLine].filter(line => line !== null)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <section className={styles.panel} aria-label="Sing this back">
      <div className={styles.header}>
        <h2
          ref={headingRef}
          className={styles.title}
          tabIndex={-1}
          aria-describedby={lines.length > 0 ? statusId : undefined}
        >
          {target.title}
        </h2>
        {position ? <span className={styles.position}>{position}</span> : null}
      </div>
      {target.subLabel ? <p className={styles.subLabel}>{target.subLabel}</p> : null}
      {lines.length > 0 ? (
        <div id={statusId} className={styles.lines}>
          {lines.map(line => (
            <p key={line} className={styles.line}>
              {line}
            </p>
          ))}
        </div>
      ) : null}

      <span className={styles.roll} aria-hidden="true">
        <MelodyMiniRoll melody={target.audition} interactive={false} showTempo={false} />
      </span>

      <div className={styles.status} role="status" data-phase={phase ?? 'idle'}>
        {phase === null ? 'Ready when you are' : phaseText[phase]}
      </div>
      {isListening ? (
        <>
          <div
            className={styles.progress}
            role="progressbar"
            aria-label="Listening"
            style={{ '--capture-ms': `${captureMs}ms` } as CSSProperties}
          >
            <span className={phase === 'listening' ? styles.fillRunning : styles.fill} />
          </div>
          <PitchInputMeter frame={meter.frame} noiseFloorRms={meter.noiseFloorRms} deviceLabel={meter.deviceLabel} />
        </>
      ) : null}

      <div className={styles.actions}>
        {isListening ? (
          <Button variant="contained" onClick={onDone}>
            Done
          </Button>
        ) : (
          <Button variant="contained" disabled={busy} onClick={onStart}>
            Start
          </Button>
        )}
        <Button variant="outlined" disabled={busy} onClick={onHearIt}>
          Hear it
        </Button>
      </div>
      <p className={styles.hint}>Sing on &quot;la&quot; or the words — we only listen to the pitch.</p>
    </section>
  )
}
